<template>
  <div>
    <!-- Progress header reflects the selected category, mirroring the XP bar in the Stats tab. -->
    <div class="flex flex-col items-center mb-3">
      <p class="text-2xl text-gradient font-extrabold leading-tight">{{ unlockedInView }} / {{ filtered.length }}</p>
      <div class="relative w-full h-2.5 mt-1 bg-gray-600 rounded-full overflow-hidden">
        <div class="bg-gradient-to-r from-green-400 to-teal-400 h-2.5 absolute top-0 left-0 transition-all"
             :style="{ width: `${progress}%` }"></div>
      </div>
      <div class="text-xs text-gray-400 mt-0.5">unlocked</div>
    </div>

    <div class="flex flex-wrap justify-center gap-2 mb-4">
      <button v-for="tab in tabs" :key="tab.key" @click="selected = tab.key"
              class="px-3 py-1 rounded-full text-sm transition"
              :class="selected === tab.key
                ? 'bg-gradient-to-r from-green-400 to-teal-400 text-gray-900 font-semibold'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'">
        {{ tab.label }}
      </button>
    </div>

    <div class="grid grid-cols-2 gap-2.5">
      <div v-for="a in filtered" :key="a.id"
           class="relative flex flex-col items-center text-center rounded-lg px-3 py-3.5 border transition"
           :class="isUnlocked(a.id)
             ? 'unlocked-card border-teal-400/60'
             : 'bg-gray-800/40 border-gray-700/60'">
        <span v-if="!isUnlocked(a.id)" class="absolute top-1.5 right-1.5 text-xs text-gray-500">🔒</span>
        <span class="text-4xl leading-none mb-1.5" :class="{'grayscale opacity-40': !isUnlocked(a.id)}">{{ a.icon }}</span>
        <div class="font-bold text-base leading-snug" :class="isUnlocked(a.id) ? 'text-white' : 'text-gray-400'">{{ a.name }}</div>
        <div class="text-sm leading-snug mt-1" :class="isUnlocked(a.id) ? 'text-gray-300' : 'text-gray-500'">{{ a.description }}</div>
        <div v-if="rarities[a.id]" class="text-xs mt-1.5 font-medium" :class="tierClass(rarities[a.id].tier)">
          {{ rarities[a.id].label }} · {{ rarities[a.id].percent }}% of players
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {useAchievements} from "../composables/useAchievements.js";

const {achievements, isUnlocked, loadAchievementStats, rarity} = useAchievements();

onMounted(loadAchievementStats);

// Precompute each card's rarity once per stats change rather than calling rarity() repeatedly
// in the template. Reactive on the loaded global stats, so it fills in once the fetch resolves.
const rarities = computed(() => Object.fromEntries(achievements.map(a => [a.id, rarity(a.id)])));

function tierClass(tier) {
  return {
    common: "text-gray-400",
    uncommon: "text-green-400",
    rare: "text-sky-400",
    legendary: "text-fuchsia-400",
  }[tier] ?? "text-gray-400";
}

const tabs = [
  {key: "all", label: "All"},
  {key: "skill", label: "Skill"},
  {key: "runs", label: "Runs"},
  {key: "streaks", label: "Streaks"},
  {key: "score", label: "Score"},
  {key: "xp", label: "XP"},
  {key: "days", label: "Days"},
  {key: "account", label: "Account"},
];

const selected = ref("all");

const filtered = computed(() =>
    selected.value === "all"
        ? achievements
        : achievements.filter(a => a.category === selected.value)
);

const unlockedInView = computed(() => filtered.value.filter(a => isUnlocked(a.id)).length);

const progress = computed(() =>
    filtered.value.length ? (unlockedInView.value / filtered.value.length) * 100 : 0
);
</script>

<style scoped>
/* Mirrors the achievement unlock toast so earned achievements share its teal glow. */
.unlocked-card {
  background: linear-gradient(135deg, #0f2a2a 0%, #111827 60%);
  box-shadow: 0 0 0 1px rgba(45, 212, 191, 0.15),
              0 0 16px rgba(45, 212, 191, 0.2);
}
</style>
